/* eslint-disable react-hooks/exhaustive-deps */
import { Box, createTheme, Drawer, IconButton, InputAdornment, Paper, Popover, Stack, TextField } from "@mui/material";
import { PaintBucket, Pipette } from "lucide-react";
import { ChangeEvent, useMemo, useState } from "react";
import InputMask from 'react-input-mask';
import { HexAlphaColorPicker } from "react-colorful";
import { colorRegex } from "@/constants/global";
import useEyeDropper from "use-eye-dropper";


interface Props {
    name?: string,
    value: string,
    condensed?: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onEyeDropperClicked?: () => void;
}

const DEFAULT_VALUE = '#C0C0C0';

const ColorPicker = ({
    name,
    value = DEFAULT_VALUE,
    condensed = false,
    onChange,
    onEyeDropperClicked,
}: Props) => {
    const { palette } = createTheme();
    const eyeDropper = useEyeDropper();
    const [debounce, setDebounce] = useState(setTimeout(() => {
        //
    }, 500));
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    
    const open = Boolean(anchorEl);
    const id = open ? 'color-popover' : undefined;

    const colors = useMemo(() => {
        const color = palette.augmentColor({
            color: {
                main: value.match(colorRegex) ? value : DEFAULT_VALUE,
            },
        })
        return color;
    }, [value])

    const handleOpenColorPicker = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleColorPickerChange = (value: string) => {
      if (debounce) {
        clearTimeout(debounce);
      }
      
      setDebounce(
        setTimeout(() => {
          if (onChange) {
            onChange({ target: { value, name }} as ChangeEvent<HTMLInputElement>);
          }
        }, 100)
      )
    }

    return (
        <Paper sx={{
            overflow: 'hidden',
            borderRadius: '12px',
        }}>
            <Stack direction="row">
                <Box height={condensed ? 50 : 100} flex={1} sx={{ backgroundColor: colors.light }} />
                <Box height={condensed ? 50 : 100} flex={1} sx={{ backgroundColor: colors.main }} />
                <Box height={condensed ? 50 : 100} flex={1} sx={{ backgroundColor: colors.dark }} />
            </Stack>


            <InputMask
                mask="#******"
                maskChar={null}
                placeholder={DEFAULT_VALUE}
                alwaysShowMask
                value={value}
                onChange={onChange}
            >
                {() => (
                    <TextField
                        fullWidth
                        value={value}
                        onChange={onChange}
                        slotProps={{
                            input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    {eyeDropper.isSupported() ? (
                                        <IconButton
                                            onClick={() => {
                                                onEyeDropperClicked?.();

                                                eyeDropper.open().then((color) => {
                                                    handleColorPickerChange(color.sRGBHex);
                                                });
                                            }}
                                        >
                                            <Pipette color={palette.primary.main}/>
                                        </IconButton>
                                    ) : null}
                                    <IconButton onClick={handleOpenColorPicker}>
                                        <PaintBucket color={palette.primary.main} />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            },
                        }}
                    />
                )}
            </InputMask>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                id={id}
        
                anchorReference="anchorEl"
                anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
                }}
                transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
                }}
                sx={{
                '& .MuiPopover-paper': {
                    backgroundColor: 'transparent',
                }
                }}
            >
                <HexAlphaColorPicker
                    color={value}
                    onChange={handleColorPickerChange}
                    style={{
                        width: 250,
                        height: 200,
                    }}
                />
            </Popover>

        </Paper>
    )
}

export default ColorPicker;
